import { decodeDoc, encodeDoc } from '../io/serialize';
import type { CanvasDoc } from '../store/types';

/**
 * Source-agnostic doc access for the isolated frame-preview tab.
 *
 * Today the doc lives in localStorage (same-origin tabs share it). The preview
 * tab cold-opens from there and stays live via BroadcastChannel (instant,
 * pushed by the editor tab on every save) with a `storage`-event fallback.
 *
 * SEAM: when project persistence moves to a DB, swap `loadPreviewDoc`'s body
 * for the fetch — boot + live-update plumbing stay source-agnostic.
 */

const KEY = 'codeframe.doc.v2';
const LEGACY_KEY = 'codeframe.doc.v1';
const CHANNEL = 'codeframe.doc';

/** Cold-open: the latest persisted doc, or null if none. */
export function loadPreviewDoc(): CanvasDoc | null {
  try {
    const raw = localStorage.getItem(KEY) ?? localStorage.getItem(LEGACY_KEY);
    return raw ? decodeDoc(raw) : null;
  } catch {
    return null;
  }
}

let channel: BroadcastChannel | null = null;
function chan(): BroadcastChannel | null {
  if (channel) return channel;
  if (typeof BroadcastChannel === 'undefined') return null;
  channel = new BroadcastChannel(CHANNEL);
  return channel;
}

/**
 * Editor side: push the doc to any open preview tabs. BroadcastChannel never
 * echoes to the posting context, so this is safe to call from the save loop.
 * No-op where BroadcastChannel is unsupported (preview falls back to `storage`).
 */
export function broadcastDoc(doc: CanvasDoc): void {
  try {
    chan()?.postMessage({ t: 'doc', doc: encodeDoc(doc) });
  } catch {
    // ignore — storage event still covers cross-tab updates
  }
}

/**
 * Preview side: subscribe to live doc updates. Combines BroadcastChannel
 * (instant) with the `storage` event (fires in other tabs on localStorage
 * write) so a frame mirrors the editor in real time. Returns an unsubscribe.
 */
export function subscribePreviewDoc(onDoc: (doc: CanvasDoc) => void): () => void {
  const c = chan();
  const onMsg = (e: MessageEvent) => {
    const data = e.data as { t?: string; doc?: string } | null;
    if (data?.t === 'doc' && typeof data.doc === 'string') {
      try {
        onDoc(decodeDoc(data.doc));
      } catch {
        /* ignore malformed */
      }
    }
  };
  c?.addEventListener('message', onMsg);

  const onStorage = (e: StorageEvent) => {
    if ((e.key === KEY || e.key === LEGACY_KEY) && e.newValue) {
      try {
        onDoc(decodeDoc(e.newValue));
      } catch {
        /* ignore malformed */
      }
    }
  };
  window.addEventListener('storage', onStorage);

  return () => {
    c?.removeEventListener('message', onMsg);
    window.removeEventListener('storage', onStorage);
  };
}
